// What the firm asks for when it creates or edits a diligencia (REC-1966): the
// actuation, the inscription of a Conservador filing, the kind of address and
// the recipients. The form keeps them as typed; this module checks them with
// the API's rules (`task-actuation.schema.ts`) and builds the body of
// POST /tasks/by-court and PATCH /tasks/:id.

import { ACTUATION_LABELS, ADDRESS_TYPE_LABELS } from '@/lib/taskActuation';

// In the order the select offers them: the common ones first, OTRA last.
export const ACTUATION_OPTIONS = [
  'NOTIFICACION_PERSONAL',
  'BUSQUEDA_Y_CEDULA_44',
  'NOTIFICACION_CEDULA',
  'REQUERIMIENTO_PAGO_EMBARGO',
  'GESTION_CONSERVADOR',
  'OTRA',
].map((value) => ({ value, label: ACTUATION_LABELS[value] }));

export const ADDRESS_TYPE_OPTIONS = [
  'PARTICULAR',
  'LABORAL',
  'INSTITUCION',
].map((value) => ({ value, label: ADDRESS_TYPE_LABELS[value] }));

// The API's caps (TASK_RECIPIENTS_MAX, TASK_RECIPIENT_REPRESENTATIVES_MAX).
export const RECIPIENTS_MAX = 20;
export const REPRESENTATIVES_MAX = 100;
const OTHER_MAX = 200;
const REGISTRO_MAX = 50;

const FOJAS_RE = /^(\d{1,10})(\s?(?:v|vta|vuelta)\.?)?$/i;

let nextKey = 0;
function key() {
  nextKey += 1;
  return `r${nextKey}`;
}

const text = (v) => (typeof v === 'string' ? v : '');

// A stored domicile keeps the commune and region it came with, as the task's
// own address does: the API takes any text there, and one outside the catalog
// is sent back as it was while the field is not touched.
function domicileFromRecipient(r) {
  if (!r?.address) return null;
  const comunaText = [r.commune, r.region].filter(Boolean).join(' · ');
  return {
    address: text(r.address),
    addressDetails: text(r.addressDetails),
    comunaText,
    addressType: r.addressType ?? '',
    stored:
      r.commune && r.region
        ? { comuna: r.commune, region: r.region, text: comunaText }
        : null,
  };
}

// The domicile's commune: from the catalog, or the stored one while untouched.
function domicileComuna(domicile, resolveComuna) {
  const fromList = resolveComuna(domicile.comunaText);
  if (fromList) return fromList;
  const { stored } = domicile;
  return stored && domicile.comunaText === stored.text ? stored : null;
}

export function emptyDomicile() {
  return { address: '', addressDetails: '', comunaText: '', addressType: '' };
}

export function newRecipient(kind) {
  return {
    key: key(),
    kind,
    representatives: kind === 'EMPRESA' ? 1 : null,
    domicile: null,
  };
}

// The persons first, then the companies, each group in the order it was
// added. The counters edit the groups, and the receptor prices the recipients
// by position, so the position has to follow from the groups.
export function orderRecipients(list) {
  return [
    ...list.filter((r) => r.kind !== 'EMPRESA'),
    ...list.filter((r) => r.kind === 'EMPRESA'),
  ];
}

// The form's state from a stored task, or a new one: no actuation chosen and
// one natural person, the most common request.
export function actuacionFromTask(task = {}) {
  const recipients = Array.isArray(task.recipients) ? task.recipients : [];
  const c = task.conservador ?? {};
  return {
    actuationType: task.actuationType ?? '',
    actuationTypeOther: text(task.actuationTypeOther),
    conservador: {
      fojas: text(c.fojas),
      numero: text(c.numero),
      anio: text(c.anio),
      registro: text(c.registro),
    },
    addressType: task.addressType ?? '',
    recipients: orderRecipients(
      recipients.length
        ? recipients.map((r) => ({
            key: key(),
            kind: r?.kind === 'EMPRESA' ? 'EMPRESA' : 'PERSONA',
            representatives:
              r?.kind === 'EMPRESA'
                ? Math.max(1, Number(r.representatives) || 1)
                : null,
            domicile: domicileFromRecipient(r),
          }))
        : [newRecipient('PERSONA')],
    ),
  };
}

// "Destinatario 2 · Empresa con 3 representantes", as the receptor reads the
// recipient when pricing it.
export function recipientTitle(r, index) {
  const n = r.representatives;
  const who =
    r.kind === 'EMPRESA'
      ? `Empresa con ${n} ${n === 1 ? 'representante' : 'representantes'}`
      : 'Persona natural';
  return `Destinatario ${index + 1} · ${who}`;
}

// The errors of what was typed, by field. Recipient errors are keyed by the
// recipient's key, so they follow it when the list changes.
export function validateActuacion(state, resolveComuna) {
  const e = {};
  if (!state.actuationType) e.actuationType = 'Elige la actuación.';
  if (state.actuationType === 'OTRA') {
    const other = state.actuationTypeOther.trim();
    if (!other) e.actuationTypeOther = 'Describe la actuación.';
    else if (other.length > OTHER_MAX)
      e.actuationTypeOther = `La descripción no puede superar ${OTHER_MAX} caracteres.`;
  }
  if (state.actuationType === 'GESTION_CONSERVADOR') {
    const c = state.conservador;
    if (!FOJAS_RE.test(c.fojas.trim()))
      e.fojas = 'Fojas lleva solo dígitos, y "vta" si es el reverso.';
    if (!/^\d{1,10}$/.test(c.numero.trim()))
      e.numero = 'El número lleva solo dígitos.';
    if (!/^\d{4}$/.test(c.anio.trim())) e.anio = 'El año lleva 4 dígitos.';
    const registro = c.registro.trim();
    if (!registro) e.registro = 'Indica el registro.';
    else if (registro.length > REGISTRO_MAX)
      e.registro = `El registro no puede superar ${REGISTRO_MAX} caracteres.`;
  }
  if (state.recipients.length === 0)
    e.recipients = 'Indica al menos un destinatario.';
  for (const r of state.recipients) {
    if (!r.domicile) continue;
    if (!r.domicile.address.trim())
      e[`${r.key}.address`] = 'Indica la dirección.';
    if (!r.domicile.comunaText.trim())
      e[`${r.key}.comuna`] = 'Indica la comuna y la región.';
    else if (!domicileComuna(r.domicile, resolveComuna))
      e[`${r.key}.comuna`] = 'Elige una comuna de la lista.';
  }
  return e;
}

// The body fields. Null where a field does not apply: the create reads null
// as absent, and the draft edit clears with it.
export function actuacionPayload(state, resolveComuna) {
  const type = state.actuationType;
  const c = state.conservador;
  return {
    actuationType: type || null,
    actuationTypeOther:
      type === 'OTRA' ? state.actuationTypeOther.trim() : null,
    conservador:
      type === 'GESTION_CONSERVADOR'
        ? {
            fojas: c.fojas.trim(),
            numero: c.numero.trim(),
            anio: c.anio.trim(),
            registro: c.registro.trim(),
          }
        : null,
    addressType: state.addressType || null,
    recipients: state.recipients.map((r) => {
      const out = { kind: r.kind };
      if (r.kind === 'EMPRESA') out.representatives = r.representatives;
      if (r.domicile) {
        const where = domicileComuna(r.domicile, resolveComuna);
        out.address = r.domicile.address.trim();
        out.addressDetails = r.domicile.addressDetails.trim() || null;
        out.commune = where?.comuna;
        out.region = where?.region;
        out.addressType = r.domicile.addressType || null;
      }
      return out;
    }),
  };
}
